import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  PhoneXMarkIcon,
  MicrophoneIcon,
  ChevronUpIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import {
  MicrophoneIcon as MicrophoneIconSolid
} from '@heroicons/react/24/solid';
import { Button } from 'components/ui';

const MiniCallWidget = ({
  isVisible,
  callState,
  callDuration,
  leadData,
  isMuted,
  isRecording,
  onToggleMute,
  onEndCall,
  onExpandCall,
  formatDuration,
  isWebCall = true // Default to true for backward compatibility
}) => {
  const getInitialPosition = () => ({
    x: window.innerWidth - 320,
    y: window.innerHeight - 200
  });

  const [position, setPosition] = useState(getInitialPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const widgetRef = useRef(null);
  const previousHeightRef = useRef(0);

  // Reset position when call state changes to idle or when a new call starts
  useEffect(() => {
    if (callState === 'idle' || callState === 'dialing') {
      setPosition(getInitialPosition());
    }
  }, [callState]);

  // Adjust position when widget size changes (when recording status appears/disappears)
  useEffect(() => {
    if (widgetRef.current) {
      const widgetHeight = widgetRef.current.offsetHeight;
      const widgetWidth = widgetRef.current.offsetWidth;
      const heightDiff = widgetHeight - previousHeightRef.current;

      // Update previous height
      previousHeightRef.current = widgetHeight;

      // If height increased and we might go off screen, adjust position
      if (heightDiff > 0) {
        let newPosition = { ...position };
        let adjusted = false;

        // Check bottom edge - move up if needed
        if (position.y + widgetHeight > window.innerHeight) {
          newPosition.y = Math.max(10, window.innerHeight - widgetHeight - 10);
          adjusted = true;
        }

        // Check right edge - move left if needed
        if (position.x + widgetWidth > window.innerWidth) {
          newPosition.x = Math.max(10, window.innerWidth - widgetWidth - 10);
          adjusted = true;
        }

        // Check top edge - ensure not negative
        if (newPosition.y < 10) {
          newPosition.y = 10;
          adjusted = true;
        }

        // Check left edge - ensure not negative
        if (newPosition.x < 10) {
          newPosition.x = 10;
          adjusted = true;
        }

        if (adjusted) {
          setPosition(newPosition);
        }
      }
    }
  }, [isRecording, position]);

  const handleMouseDown = (e) => {
    // Don't drag if clicking on buttons or interactive elements
    if (e.target.closest('button') || e.target.closest('[role="button"]')) {
      return;
    }

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      let newX = Math.max(0, Math.min(window.innerWidth - 300, e.clientX - dragStart.x));
      let newY = Math.max(0, Math.min(window.innerHeight - 200, e.clientY - dragStart.y));

      // Snap to edges (within 20px)
      const snapDistance = 20;
      if (newX < snapDistance) newX = 0;
      if (newX > window.innerWidth - 300 - snapDistance) newX = window.innerWidth - 300;
      if (newY < snapDistance) newY = 0;
      if (newY > window.innerHeight - 200 - snapDistance) newY = window.innerHeight - 200;

      setPosition({ x: newX, y: newY });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Early return after all hooks
  if (!isVisible || (callState !== 'connected' && callState !== 'dialing')) {
    return null;
  }

  return (
    <div
      ref={widgetRef}
      className={`fixed z-[9999999] bg-white dark:bg-dark-700 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 p-4 min-w-[300px] ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        userSelect: isDragging ? 'none' : 'auto'
      }}
      onMouseDown={handleMouseDown}
      title="Drag to move (avoid buttons)"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {callState === 'dialing' ? 'Dialing...' : 'Call in Progress'}
          </span>
        </div>
        <button
          onClick={onExpandCall}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
          title="Expand call controls"
        >
          <ChevronUpIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Lead Info */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-blue-600 dark:text-blue-300">
            {leadData?.name?.charAt(0)?.toUpperCase() || 'L'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {leadData?.name || leadData?.fullName || leadData?.leadname || leadData?.phone || leadData?.mobile || 'Unknown Contact'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {leadData?.mobile || leadData?.phone || leadData?.phoneNumber || 'No phone'}
          </p>
        </div>
      </div>

      {/* Call Duration */}
      <div className="flex items-center justify-center gap-2 mb-3 p-2 bg-green-50 dark:bg-green-900/20 rounded">
        <ClockIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
        <span className="text-sm font-mono font-bold text-green-700 dark:text-green-300">
          {formatDuration(callDuration)}
        </span>
      </div>

      {/* Recording Status */}
      {isRecording && (
        <div className="flex items-center justify-center gap-2 mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <span className="text-xs font-medium text-red-700 dark:text-red-300">
            Recording Active
          </span>
        </div>
      )}

      {/* Mini Controls - Only for Web Calls */}
      {isWebCall ? (
        <div className="flex items-center justify-between gap-2">
          {/* Mute Button */}
          <button
            onClick={onToggleMute}
            className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-colors ${isMuted
              ? 'bg-red-500 text-white'
              : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
              }`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <>
                <MicrophoneIconSolid className="w-4 h-4" />
                {/* Diagonal slash line */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-6 w-0.5 bg-white rotate-45 rounded-full"></div>
                </div>
              </>
            ) : (
              <MicrophoneIcon className="w-4 h-4" />
            )}
          </button>

          {/* End Call Button */}
          <Button
            color="error"
            size="sm"
            onClick={onEndCall}
            className="flex-1 rounded-full px-4 py-2"
          >
            <PhoneXMarkIcon className="w-4 h-4 mr-1" />
            End Call
          </Button>
        </div>
      ) : (
        <div className="rounded border border-blue-200 bg-blue-50 p-2 text-center dark:border-blue-800 dark:bg-blue-900/20">
          <span className="text-[10px] sm:text-xs font-medium text-blue-700 dark:text-blue-300">
            📝 Note: To manage the call (mute/hang up), use the Ozonetel Dialer.
          </span>
        </div>
      )}
    </div>
  );
};

export default MiniCallWidget;
